import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Calendar, Clock, User, Filter, Edit, Trash2, CheckCircle, XCircle, RefreshCw, CalendarDays, UserCheck, Eye, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Appointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [checkInChiefComplaint, setCheckInChiefComplaint] = useState('');
  
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: '',
    appointment_date: '',
    appointment_time: '',
    reason: '',
    notes: ''
  });

  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, patientsRes, doctorsRes, statsRes] = await Promise.all([
        api.get('/appointments'),
        api.get('/patients'),
        api.get('/users/doctors'),
        api.get('/appointments/stats')
      ]);
      setAppointments(appointmentsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      doctor_id: '',
      appointment_date: '',
      appointment_time: '',
      reason: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/appointments', formData);
      toast.success('Appointment scheduled successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule appointment');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedAppointment) return;
    
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, formData);
      toast.success('Appointment updated successfully!');
      setEditDialogOpen(false);
      setSelectedAppointment(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    }
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;
    
    try {
      await api.delete(`/appointments/${selectedAppointment.id}`);
      toast.success('Appointment deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleStatusUpdate = async (appointmentId, status) => {
    try {
      await api.put(`/appointments/${appointmentId}/status?status=${status}`);
      toast.success(`Appointment ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update appointment status');
    }
  };

  const openCheckInDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setCheckInChiefComplaint(appointment.reason || '');
    setCheckInDialogOpen(true);
  };

  const handleCheckIn = async () => {
    if (!selectedAppointment) return;
    
    try {
      const response = await api.post(
        `/appointments/${selectedAppointment.id}/convert-to-visit?chief_complaint=${encodeURIComponent(checkInChiefComplaint || selectedAppointment.reason)}&priority=normal`
      );
      toast.success('Patient checked in successfully!');
      setCheckInDialogOpen(false);
      setSelectedAppointment(null);
      setCheckInChiefComplaint('');
      fetchData();
      
      // Navigate to the new visit
      if (response.data.visit?.id) {
        navigate(`/visits/${response.data.visit.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in patient');
    }
  };

  const openEditDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      patient_id: appointment.patient_id,
      doctor_id: appointment.doctor_id,
      appointment_date: appointment.appointment_date,
      appointment_time: appointment.appointment_time,
      reason: appointment.reason,
      notes: appointment.notes || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setDeleteDialogOpen(true);
  };

  const openViewDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setViewDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-purple-200 text-blue-700 border-blue-200';
      case 'completed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'cancelled':
        return 'bg-rose-100 text-rose-700 border-rose-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isPast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(apt => {
    const matchesStatus = statusFilter === 'all' || apt.status === statusFilter;
    const matchesDate = !dateFilter || apt.appointment_date === dateFilter;
    const matchesDoctor = doctorFilter === 'all' || apt.doctor_id === doctorFilter;
    return matchesStatus && matchesDate && matchesDoctor;
  });

  // Sort by date and time
  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    if (a.appointment_date !== b.appointment_date) {
      return b.appointment_date.localeCompare(a.appointment_date);
    }
    return a.appointment_time.localeCompare(b.appointment_time);
  });

  // Get today's appointments
  const todaysAppointments = appointments.filter(
    apt => isToday(apt.appointment_date) && apt.status === 'scheduled'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="appointments-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Appointments
            </h1>
            <p className="text-slate-600 mt-2">Schedule and manage patient appointments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="schedule-appointment-button" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Schedule Appointment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Schedule New Appointment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_id">Patient *</Label>
                    <PatientSearchDropdown
                      patients={patients}
                      selectedPatientId={formData.patient_id}
                      onSelect={(patientId) => setFormData({...formData, patient_id: patientId})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="doctor_id">Doctor *</Label>
                    <Select value={formData.doctor_id} onValueChange={(value) => setFormData({...formData, doctor_id: value})} required>
                      <SelectTrigger data-testid="appointment-doctor-select">
                        <SelectValue placeholder="Select doctor" />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map((doctor) => (
                          <SelectItem key={doctor.id} value={doctor.id}>
                            Dr. {doctor.full_name} {doctor.specialization && `(${doctor.specialization})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="appointment_date">Date *</Label>
                      <Input
                        id="appointment_date"
                        type="date"
                        value={formData.appointment_date}
                        onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="appointment-date-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="appointment_time">Time *</Label>
                      <Select value={formData.appointment_time} onValueChange={(value) => setFormData({...formData, appointment_time: value})} required>
                        <SelectTrigger data-testid="appointment-time-input">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
                            '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                            '16:00', '16:30', '17:00', '17:30'].map(time => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason *</Label>
                    <Select value={formData.reason} onValueChange={(value) => setFormData({...formData, reason: value})} required>
                      <SelectTrigger data-testid="appointment-reason-input">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Regular checkup">Regular checkup</SelectItem>
                        <SelectItem value="Follow-up visit">Follow-up visit</SelectItem>
                        <SelectItem value="Consultation">Consultation</SelectItem>
                        <SelectItem value="Lab results review">Lab results review</SelectItem>
                        <SelectItem value="Prescription refill">Prescription refill</SelectItem>
                        <SelectItem value="Urgent care">Urgent care</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Additional notes..."
                    />
                  </div>

                  <Button type="submit" data-testid="submit-appointment-button" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    Schedule Appointment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <Card className="border border-blue-200 bg-purple-100">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.total_scheduled || 0}</p>
              <p className="text-xs text-purple-700">Scheduled</p>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{stats.total_completed || 0}</p>
              <p className="text-xs text-emerald-600">Completed</p>
            </CardContent>
          </Card>
          <Card className="border border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-rose-700">{stats.total_cancelled || 0}</p>
              <p className="text-xs text-rose-600">Cancelled</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{stats.today_count || 0}</p>
              <p className="text-xs text-amber-600">Today</p>
            </CardContent>
          </Card>
          <Card className="border border-purple-200 bg-indigo-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{stats.upcoming_week || 0}</p>
              <p className="text-xs text-purple-600">This Week</p>
            </CardContent>
          </Card>
        </div>

        {/* Today's Appointments Highlight */}
        {todaysAppointments.length > 0 && (
          <Card className="mb-6 border-2 border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <CalendarDays className="h-5 w-5" />
                Today's Appointments ({todaysAppointments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {todaysAppointments.map(apt => {
                  const patient = patients.find(p => p.id === apt.patient_id);
                  return (
                    <Badge key={apt.id} variant="outline" className="bg-white px-3 py-2 text-sm">
                      <Clock className="h-3 w-3 mr-1" />
                      {apt.appointment_time} - {patient?.full_name || 'Unknown'}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>
              
              <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
                <TabsList className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 h-auto">
                  <TabsTrigger 
                    value="all" 
                    className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all text-sm"
                  >
                    All
                  </TabsTrigger>
                  <TabsTrigger 
                    value="scheduled" 
                    className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all text-sm"
                  >
                    Scheduled
                  </TabsTrigger>
                  <TabsTrigger 
                    value="completed" 
                    className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all text-sm"
                  >
                    Completed
                  </TabsTrigger>
                  <TabsTrigger 
                    value="cancelled" 
                    className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all text-sm"
                  >
                    Cancelled
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-auto"
                placeholder="Filter by date"
              />

              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map(doctor => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(dateFilter || doctorFilter !== 'all') && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFilter(''); setDoctorFilter('all'); }}>
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Appointments List */}
        <div className="space-y-4">
          {sortedAppointments.length === 0 ? (
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-12 text-center">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {appointments.length === 0 ? 'No appointments scheduled' : 'No appointments match your filters'}
                </p>
              </CardContent>
            </Card>
          ) : (
            sortedAppointments.map((appointment, index) => {
              const patient = patients.find(p => p.id === appointment.patient_id);
              const doctor = doctors.find(d => d.id === appointment.doctor_id);
              const today = isToday(appointment.appointment_date);
              const past = isPast(appointment.appointment_date);
              
              return (
                <motion.div
                  key={appointment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                  <Card 
                    className={`border shadow-sm hover:shadow-md transition-all bg-white cursor-pointer ${
                      today ? 'border-l-4 border-l-amber-500' : ''
                    } ${past && appointment.status === 'scheduled' ? 'opacity-60' : ''}`}
                    onClick={() => openViewDialog(appointment)}
                    data-testid={`appointment-card-${appointment.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                              <User className="h-5 w-5 text-[#4A238F]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">
                                {patient?.full_name || 'Unknown Patient'}
                              </h3>
                              <p className="text-sm text-slate-600">
                                Dr. {doctor?.full_name || 'Unknown Doctor'}
                                {doctor?.specialization && ` • ${doctor.specialization}`}
                              </p>
                            </div>
                          </div>
                          <p className="text-slate-700 mb-2">{appointment.reason}</p>
                          {appointment.notes && (
                            <p className="text-sm text-slate-500 mb-2 italic">"{appointment.notes}"</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(appointment.appointment_date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              {today && <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Today</Badge>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {appointment.appointment_time}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3" onClick={(e) => e.stopPropagation()}>
                          <Badge className={`${getStatusColor(appointment.status)} border`}>
                            {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                          </Badge>
                          
                          {appointment.status === 'scheduled' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                onClick={() => openCheckInDialog(appointment)}
                                data-testid={`checkin-appointment-${appointment.id}`}
                              >
                                <UserCheck className="h-4 w-4 mr-1" />
                                Check-In
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleStatusUpdate(appointment.id, 'completed')}
                                data-testid={`complete-appointment-${appointment.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => handleStatusUpdate(appointment.id, 'cancelled')}
                                data-testid={`cancel-appointment-${appointment.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          )}
                          
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openViewDialog(appointment)}
                              title="View Details"
                              data-testid={`view-appointment-${appointment.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {appointment.status === 'scheduled' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditDialog(appointment)}
                                title="Edit/Reschedule"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-rose-600"
                              onClick={() => openDeleteDialog(appointment)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit / Reschedule Appointment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Patient</Label>
                <PatientSearchDropdown
                  patients={patients}
                  selectedPatientId={formData.patient_id}
                  onSelect={(patientId) => setFormData({...formData, patient_id: patientId})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Doctor</Label>
                <Select value={formData.doctor_id} onValueChange={(value) => setFormData({...formData, doctor_id: value})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        Dr. {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Select value={formData.appointment_time} onValueChange={(value) => setFormData({...formData, appointment_time: value})} required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
                        '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                        '16:00', '16:30', '17:00', '17:30'].map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={formData.reason} onValueChange={(value) => setFormData({...formData, reason: value})} required>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Regular checkup">Regular checkup</SelectItem>
                    <SelectItem value="Follow-up visit">Follow-up visit</SelectItem>
                    <SelectItem value="Consultation">Consultation</SelectItem>
                    <SelectItem value="Lab results review">Lab results review</SelectItem>
                    <SelectItem value="Prescription refill">Prescription refill</SelectItem>
                    <SelectItem value="Urgent care">Urgent care</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                />
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Appointment</DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Check-In Dialog */}
        <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-purple-600" />
                Check-In Patient
              </DialogTitle>
            </DialogHeader>
            {selectedAppointment && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Patient</span>
                    <span className="font-medium">
                      {patients.find(p => p.id === selectedAppointment.patient_id)?.full_name || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Appointment</span>
                    <span className="font-medium">
                      {selectedAppointment.appointment_date} at {selectedAppointment.appointment_time}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Doctor</span>
                    <span className="font-medium">
                      {doctors.find(d => d.id === selectedAppointment.doctor_id)?.full_name || 'Unassigned'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Chief Complaint</Label>
                  <Input
                    value={checkInChiefComplaint}
                    onChange={(e) => setCheckInChiefComplaint(e.target.value)}
                    placeholder="Enter reason for visit..."
                    data-testid="checkin-complaint"
                  />
                  <p className="text-xs text-slate-500">
                    This will create a new OPD visit and assign a token number
                  </p>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setCheckInDialogOpen(false)} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCheckIn}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="confirm-checkin"
                  >
                    <UserCheck className="mr-2 h-4 w-4" />
                    Check-In & Create Visit
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Appointment Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#4A238F]" />
                Appointment Details
              </DialogTitle>
            </DialogHeader>
            {selectedAppointment && (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex justify-between items-center">
                  <Badge className={`${getStatusColor(selectedAppointment.status)} text-sm px-3 py-1`}>
                    {selectedAppointment.status.charAt(0).toUpperCase() + selectedAppointment.status.slice(1)}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    ID: {selectedAppointment.id?.slice(0, 8)}...
                  </span>
                </div>

                {/* Date & Time */}
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Date</p>
                      <p className="font-semibold text-slate-900">
                        {new Date(selectedAppointment.appointment_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Time</p>
                      <p className="font-semibold text-slate-900">{selectedAppointment.appointment_time}</p>
                    </div>
                  </div>
                </div>

                {/* Patient Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Patient Information</p>
                  {(() => {
                    const patient = patients.find(p => p.id === selectedAppointment.patient_id);
                    return patient ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-500" />
                          <span className="font-medium">{patient.full_name}</span>
                          <Badge variant="outline" className="text-xs">{patient.patient_number}</Badge>
                        </div>
                        {patient.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="h-4 w-4" />
                            <span>{patient.phone}</span>
                          </div>
                        )}
                        {patient.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="h-4 w-4" />
                            <span>{patient.email}</span>
                          </div>
                        )}
                        {patient.address && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="h-4 w-4" />
                            <span>{patient.address}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">Patient information not available</p>
                    );
                  })()}
                </div>

                {/* Doctor Info */}
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Doctor</p>
                  {(() => {
                    const doctor = doctors.find(d => d.id === selectedAppointment.doctor_id);
                    return doctor ? (
                      <p className="text-slate-900">
                        Dr. {doctor.full_name}
                        {doctor.specialization && <span className="text-slate-500"> • {doctor.specialization}</span>}
                      </p>
                    ) : (
                      <p className="text-slate-500">Unassigned</p>
                    );
                  })()}
                </div>

                {/* Reason */}
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Reason for Visit</p>
                  <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedAppointment.reason}</p>
                </div>

                {/* Notes */}
                {selectedAppointment.notes && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Notes</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded italic">{selectedAppointment.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <DialogFooter className="gap-2 pt-4 border-t">
                  {selectedAppointment.status === 'scheduled' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setViewDialogOpen(false);
                          openEditDialog(selectedAppointment);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => {
                          setViewDialogOpen(false);
                          openCheckInDialog(selectedAppointment);
                        }}
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Check-In
                      </Button>
                    </>
                  )}
                  {selectedAppointment.status !== 'scheduled' && (
                    <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                      Close
                    </Button>
                  )}
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
